import {
  AlertCircle,
  BarChart3,
  Bell,
  Check,
  ChevronRight,
  KeyRound,
  Link2,
  Loader2,
  MessageSquare,
  Monitor,
  Moon,
  Palette,
  Puzzle,
  RotateCcw,
  Save,
  Server,
  Shield,
  Sparkles,
  Sun,
  User,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ModelSelector } from "@/components/chat/ModelSelector";
import { AgentAvatar } from "@/components/group-chat/AgentAvatar";
import { SettingsAuthorizationsTab } from "@/components/settings/SettingsAuthorizationsTab";
import { SettingsExtensionsTab } from "@/components/settings/SettingsExtensionsTab";
import { SettingsIntegrationsTab } from "@/components/settings/SettingsIntegrationsTab";
import { SettingsUsageTab } from "@/components/settings/SettingsUsageTab";
import { ApiKeysManager } from "@/components/shared/ApiKeysManager";
import { HarnessJobsManager } from "@/components/shared/HarnessJobsManager";
import { HarnessWorkersManager } from "@/components/shared/HarnessWorkersManager";
import { featureFlags } from "@/config/featureFlags";
import { NOTIFICATION_TYPES } from "@/constants/notifications";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import { useTheme } from "@/context/ThemeContext";
import { groupChatService } from "@/lib/groupChatService";
import { authService, notificationService } from "@/lib/services";
import type { NotificationPreference, NotificationType, SpecialistAgent, UserPreferences } from "@/types";

type SettingsTab =
  | "profile"
  | "appearance"
  | "ai_preferences"
  | "group_chat"
  | "notifications"
  | "security"
  | "harness"
  | "teams"
  | "integrations"
  | "extensions"
  | "authorizations"
  | "usage";

const TONE_OPTIONS = [
  { value: "professional", label: "Professional", description: "Formal, business-appropriate language" },
  { value: "casual", label: "Casual", description: "Relaxed, conversational tone" },
  { value: "friendly", label: "Friendly", description: "Warm, approachable, and encouraging" },
  { value: "concise", label: "Concise", description: "Straight to the point, minimal filler" },
  { value: "direct", label: "Direct", description: "Clear and unambiguous, no hedging" },
] as const;

const LENGTH_OPTIONS = [
  { value: "brief", label: "Brief", description: "Short, focused answers" },
  { value: "moderate", label: "Moderate", description: "Balanced level of detail" },
  { value: "detailed", label: "Detailed", description: "Thorough, comprehensive responses" },
] as const;

export function Settings() {
  const { user, profile, updatePreferences } = useAuth();
  const { enabledModels, tierDefaults } = useApp();
  const { desktopPermission, requestDesktopPermission } = useNotifications();
  const { mode: currentThemeMode, setMode: setThemeMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
    const tabParam = searchParams.get("tab");
    if (
      tabParam &&
      [
        "profile",
        "appearance",
        "ai_preferences",
        "group_chat",
        "notifications",
        "security",
        "harness",
        "teams",
        "integrations",
        "extensions",
        "usage",
      ].includes(tabParam)
    ) {
      return tabParam as SettingsTab;
    }
    return "profile";
  });

  // Local state for editable settings — theme syncs with ThemeProvider
  const [theme, setThemeLocal] = useState<"light" | "dark" | "system">(
    // NOSONAR — multi-line initializer expression, not actual complexity
    currentThemeMode,
  );
  const setTheme = useCallback(
    (newTheme: "light" | "dark" | "system") => {
      setThemeLocal(newTheme);
      // Apply immediately for instant visual feedback
      setThemeMode(newTheme);
    },
    [setThemeMode],
  );
  const [responseTone, setResponseTone] = useState<UserPreferences["responseTone"]>(profile?.preferences?.responseTone);
  const [responseLength, setResponseLength] = useState<UserPreferences["responseLength"]>(
    profile?.preferences?.responseLength,
  );
  const [defaultModelId, setDefaultModelId] = useState<string | undefined>(profile?.preferences?.defaultModelId);
  const [customInstructions, setCustomInstructions] = useState(profile?.preferences?.customInstructions || "");
  const [showContextInfo, setShowContextInfo] = useState(profile?.preferences?.showContextInfo ?? false);

  // Ref-based snapshot of AI preference state so handleSave never has stale closures.
  // Update this ref whenever any preference state changes.
  const aiPrefsRef = useRef({ responseTone, responseLength, defaultModelId, customInstructions, showContextInfo });
  aiPrefsRef.current = { responseTone, responseLength, defaultModelId, customInstructions, showContextInfo };

  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState<NotificationPreference[]>([]);
  const [isLoadingNotifPrefs, setIsLoadingNotifPrefs] = useState(false);

  // Group chat bench state
  const [gcAgents, setGcAgents] = useState<SpecialistAgent[]>([]);
  const [gcBenchUuids, setGcBenchUuids] = useState<string[] | null>(null);
  const [isLoadingGcAgents, setIsLoadingGcAgents] = useState(false);

  // Initialize group chat bench from preferences
  useEffect(() => {
    const saved = profile?.preferences?.groupChatBench;
    if (saved && saved.length > 0) {
      setGcBenchUuids(saved);
    } else {
      setGcBenchUuids(null);
    }
  }, [profile?.preferences?.groupChatBench]);

  // Load group chat agents when tab changes to group_chat
  useEffect(() => {
    if (activeTab !== "group_chat") return;
    setIsLoadingGcAgents(true);
    groupChatService
      .listSpecialistAgents()
      .then((data) => setGcAgents(data))
      .catch((err) => console.error("Failed to load agents:", err))
      .finally(() => setIsLoadingGcAgents(false));
  }, [activeTab]);

  // Load notification preferences when tab changes to notifications
  useEffect(() => {
    if (activeTab !== "notifications") return;
    setIsLoadingNotifPrefs(true);
    notificationService
      .getPreferences()
      .then((res) => setNotifPrefs(res.data))
      .catch((err) => console.error("Failed to load notification preferences:", err))
      .finally(() => setIsLoadingNotifPrefs(false));
  }, [activeTab]);

  const handleNotifPrefToggle = useCallback(
    async (type: NotificationType, channel: "inAppEnabled" | "desktopEnabled", value: boolean) => {
      // If enabling desktop and permission not granted, request it
      if (channel === "desktopEnabled" && value && desktopPermission !== "granted") {
        const permission = await requestDesktopPermission();
        if (permission !== "granted") return; // User denied
      }

      // Optimistic update
      setNotifPrefs((prev) =>
        prev.map(
          (
            p, // NOSONAR — arrow callback in React state updater; complexity inherent to functional update pattern
          ) => (p.notificationType === type ? { ...p, [channel]: value } : p),
        ),
      );

      // Persist to server
      try {
        const update = {
          notificationType: type,
          [channel]: value,
        };
        const res = await notificationService.updatePreferences([update]);
        // Only update the specific preference that was changed (avoid overwriting
        // other in-flight optimistic updates from concurrent toggles)
        const updatedPref = res.data.find((p: NotificationPreference) => p.notificationType === type);
        if (updatedPref) {
          setNotifPrefs((prev) =>
            prev.map(
              (
                p, // NOSONAR — arrow callback in React state updater; complexity inherent to functional update pattern
              ) => (p.notificationType === type ? updatedPref : p),
            ),
          );
        }
      } catch (err) {
        console.error("Failed to update notification preference:", err);
        // Revert on error
        setNotifPrefs((prev) =>
          prev.map(
            (
              p, // NOSONAR — arrow callback in React state updater; complexity inherent to functional update pattern
            ) => (p.notificationType === type ? { ...p, [channel]: !value } : p),
          ),
        );
      }
    },
    [desktopPermission, requestDesktopPermission],
  );

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "success" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup save status timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveStatus("idle");
    setSaveError(null);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    try {
      const settingsToSave: Partial<UserPreferences> = {};

      // Include settings based on active tab
      if (activeTab === "appearance") {
        settingsToSave.theme = theme;
      } else if (activeTab === "ai_preferences") {
        const prefs = aiPrefsRef.current;
        // null signals "clear this preference" to the backend
        (settingsToSave as Record<string, unknown>).responseTone = prefs.responseTone ?? null;
        (settingsToSave as Record<string, unknown>).responseLength = prefs.responseLength ?? null;
        settingsToSave.defaultModelId = prefs.defaultModelId;
        settingsToSave.customInstructions = prefs.customInstructions ?? "";
        settingsToSave.showContextInfo = prefs.showContextInfo;
      } else if (activeTab === "group_chat") {
        settingsToSave.groupChatBench = gcBenchUuids && gcBenchUuids.length > 0 ? gcBenchUuids : [];
      }

      await authService.updateSettings(settingsToSave);
      updatePreferences(settingsToSave);
      setSaveStatus("success");

      // Clear success message after 3 seconds
      saveTimerRef.current = setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (err) {
      console.error("Failed to save settings:", err);
      setSaveStatus("error");
      setSaveError(err instanceof Error ? err.message : "Failed to save settings");
      saveTimerRef.current = setTimeout(() => setSaveStatus("idle"), 5000);
    } finally {
      setIsSaving(false);
    }
  }, [activeTab, theme, gcBenchUuids, updatePreferences]);

  const handleTabChange = useCallback(
    (tab: SettingsTab) => {
      setActiveTab(tab);
      setSaveStatus("idle");
      if (tab === "profile") {
        setSearchParams({});
      } else {
        setSearchParams({ tab });
      }
    },
    [setSearchParams],
  );

  const tabs = [
    { id: "profile" as const, name: "Profile", icon: User },
    { id: "appearance" as const, name: "Appearance", icon: Palette },
    { id: "ai_preferences" as const, name: "AI Preferences", icon: Sparkles },
    ...(featureFlags.brainstorm ? [{ id: "group_chat" as const, name: "Brainstorm", icon: MessageSquare }] : []),
    { id: "notifications" as const, name: "Notifications", icon: Bell },
    { id: "security" as const, name: "Security", icon: Shield },
    ...(featureFlags.rally ? [{ id: "harness" as const, name: "RALLY", icon: Server }] : []),
    { id: "teams" as const, name: "Teams", icon: Users },
    { id: "integrations" as const, name: "Integrations", icon: Link2 },
    { id: "extensions" as const, name: "Extensions", icon: Puzzle },
    { id: "authorizations" as const, name: "Authorizations", icon: KeyRound },
    { id: "usage" as const, name: "Usage", icon: BarChart3 },
  ];

  const themeOptions = [
    { id: "light" as const, name: "Light", icon: Sun },
    { id: "dark" as const, name: "Dark", icon: Moon },
    { id: "system" as const, name: "System", icon: Monitor },
  ];

  // Determine if current tab has saveable settings
  const hasSaveableSettings = ["appearance", "ai_preferences", "group_chat"].includes(activeTab);

  return (
    <div className="flex-1 flex min-h-0">
      {/* Sidebar */}
      <div className="w-64 shrink-0 border-r border-border-primary p-4">
        <h1 className="text-lg font-semibold text-content-primary px-3 mb-4">Settings</h1>
        <nav className="space-y-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-surface-secondary text-content-primary"
                    : "text-content-tertiary hover:text-content-primary hover:bg-interactive-hover"
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon className="h-4 w-4" />
                  {tab.name}
                </div>
                {isActive && <ChevronRight className="h-4 w-4" />}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-2xl">
          {/* Profile Tab */}
          {activeTab === "profile" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-content-primary">Profile</h2>
                <p className="text-content-tertiary mt-1">Manage your account information</p>
              </div>

              <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-16 w-16 rounded-full bg-brand-gradient-br flex items-center justify-center text-2xl font-semibold text-white">
                    {user?.displayName?.charAt(0) || "U"}
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-content-primary">{user?.displayName || "User"}</h3>
                    <p className="text-content-tertiary">{user?.email}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="settings-display-name"
                      className="block text-sm font-medium text-content-secondary mb-1.5"
                    >
                      Display Name
                    </label>
                    <input
                      id="settings-display-name"
                      type="text"
                      value={user?.displayName || ""}
                      readOnly
                      className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                    />
                    <p className="text-xs text-content-quaternary mt-1">Synced from your Microsoft account</p>
                  </div>
                  <div>
                    <label htmlFor="settings-email" className="block text-sm font-medium text-content-secondary mb-1.5">
                      Email
                    </label>
                    <input
                      id="settings-email"
                      type="email"
                      value={user?.email || ""}
                      readOnly
                      className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === "appearance" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-content-primary">Appearance</h2>
                <p className="text-content-tertiary mt-1">Customize the look and feel</p>
              </div>

              <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                <h3 className="text-sm font-medium text-content-secondary mb-4">Theme</h3>
                <div className="grid grid-cols-3 gap-3">
                  {themeOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = theme === option.id;
                    return (
                      <button
                        key={option.id}
                        onClick={() => setTheme(option.id)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-lg border transition-colors ${
                          isSelected
                            ? "border-brand-primary bg-brand-primary-subtle"
                            : "border-border-secondary hover:border-border-hover"
                        }`}
                      >
                        <Icon className={`h-6 w-6 ${isSelected ? "text-brand-primary" : "text-content-tertiary"}`} />
                        <span
                          className={`text-sm font-medium ${
                            isSelected ? "text-brand-primary" : "text-content-secondary"
                          }`}
                        >
                          {option.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-content-quaternary mt-3">
                  Choose between light, dark, or system (follows your OS preference). Changes apply immediately.
                </p>
              </div>
            </div>
          )}

          {/* AI Preferences Tab */}
          {activeTab === "ai_preferences" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-content-primary">AI Preferences</h2>
                <p className="text-content-tertiary mt-1">
                  Customize how AI responds to you across all chats and personas
                </p>
              </div>

              {/* Default Model */}
              <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                <h3 className="text-sm font-medium text-content-secondary mb-1">Default Model</h3>
                <p className="text-xs text-content-quaternary mb-4">
                  Choose which model is selected by default when starting a new chat
                </p>
                <ModelSelector
                  value={defaultModelId || ""}
                  onChange={(v) => setDefaultModelId(v || undefined)}
                  models={enabledModels}
                  tierDefaults={tierDefaults}
                  className="w-full"
                />
              </div>

              {/* Response Tone */}
              <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                <h3 className="text-sm font-medium text-content-secondary mb-1">Response Tone</h3>
                <p className="text-xs text-content-quaternary mb-4">Choose the overall tone for AI responses</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {TONE_OPTIONS.map((option) => {
                    const isSelected = responseTone === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => setResponseTone(isSelected ? undefined : option.value)}
                        className={`flex flex-col items-start p-3 rounded-lg border transition-colors text-left ${
                          isSelected
                            ? "border-brand-secondary bg-brand-secondary-subtle"
                            : "border-border-secondary hover:border-border-hover"
                        }`}
                      >
                        <span
                          className={`text-sm font-medium ${
                            isSelected ? "text-brand-secondary" : "text-content-secondary"
                          }`}
                        >
                          {option.label}
                        </span>
                        <span className="text-xs text-content-quaternary mt-0.5">{option.description}</span>
                      </button>
                    );
                  })}
                </div>
                {responseTone && (
                  <button
                    onClick={() => setResponseTone(undefined)}
                    className="text-xs text-content-quaternary hover:text-content-tertiary mt-3 transition-colors"
                  >
                    Clear selection
                  </button>
                )}
              </div>

              {/* Response Length */}
              <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                <h3 className="text-sm font-medium text-content-secondary mb-1">Response Length</h3>
                <p className="text-xs text-content-quaternary mb-4">Control how verbose AI responses should be</p>
                <div className="grid grid-cols-3 gap-3">
                  {LENGTH_OPTIONS.map((option) => {
                    const isSelected = responseLength === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => setResponseLength(isSelected ? undefined : option.value)}
                        className={`flex flex-col items-center gap-1.5 p-4 rounded-lg border transition-colors ${
                          isSelected
                            ? "border-brand-secondary bg-brand-secondary-subtle"
                            : "border-border-secondary hover:border-border-hover"
                        }`}
                      >
                        <span
                          className={`text-sm font-medium ${
                            isSelected ? "text-brand-secondary" : "text-content-secondary"
                          }`}
                        >
                          {option.label}
                        </span>
                        <span className="text-xs text-content-quaternary text-center">{option.description}</span>
                      </button>
                    );
                  })}
                </div>
                {responseLength && (
                  <button
                    onClick={() => setResponseLength(undefined)}
                    className="text-xs text-content-quaternary hover:text-content-tertiary mt-3 transition-colors"
                  >
                    Clear selection
                  </button>
                )}
              </div>

              {/* Custom Instructions */}
              <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                <h3 className="text-sm font-medium text-content-secondary mb-1">Custom Instructions</h3>
                <p className="text-xs text-content-quaternary mb-4">
                  Tell the AI anything specific about how you'd like it to respond. These instructions apply to all
                  conversations.
                </p>
                <textarea
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  maxLength={2000}
                  rows={5}
                  placeholder="e.g., Always provide code examples in Python. Explain concepts like I'm a senior engineer. Use bullet points for lists."
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-secondary/50 focus:border-brand-secondary resize-none"
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-content-quaternary">These are appended to every AI system prompt</p>
                  <span className="text-xs text-content-quaternary">{customInstructions.length}/2000</span>
                </div>
              </div>

              {/* Context Info Toggle */}
              <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-content-secondary">Show Context Usage</h3>
                    <p className="text-xs text-content-quaternary mt-1">
                      Display token count, context window usage, and compaction details in the chat footer
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showContextInfo}
                    onClick={() => setShowContextInfo(!showContextInfo)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      showContextInfo ? "bg-brand-primary" : "bg-surface-tertiary"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        showContextInfo ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Brainstorm Tab */}
          {activeTab === "group_chat" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-content-primary">Brainstorm</h2>
                <p className="text-content-tertiary mt-1">
                  Configure your default persona bench for brainstorm discussions
                </p>
              </div>

              <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-medium text-content-secondary">Persona Bench</h3>
                    <p className="text-xs text-content-quaternary mt-1">
                      Choose which specialist personas the orchestrator can pick from. You can also change this
                      per-session from the Brainstorm header.
                    </p>
                  </div>
                  {gcBenchUuids !== null && (
                    <button
                      onClick={() => {
                        setGcBenchUuids(null);
                      }}
                      className="flex items-center gap-1.5 text-xs text-content-tertiary hover:text-content-primary transition px-2 py-1 rounded-lg hover:bg-interactive-hover"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Reset to all
                    </button>
                  )}
                </div>

                {isLoadingGcAgents ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 text-content-tertiary animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {gcAgents.map((agent) => {
                      const inBench = gcBenchUuids === null || gcBenchUuids.includes(agent.uuid);
                      return (
                        <button
                          key={agent.uuid}
                          onClick={() => {
                            setGcBenchUuids((prev) => {
                              let next: string[] | null;
                              if (prev === null) {
                                // Switching from "all" to custom: all except this one
                                next = gcAgents.map((a) => a.uuid).filter((uuid) => uuid !== agent.uuid);
                              } else if (prev.includes(agent.uuid)) {
                                const filtered = prev.filter((uuid) => uuid !== agent.uuid);
                                if (filtered.length < 2) return prev; // minimum 2
                                next = filtered;
                              } else {
                                next = [...prev, agent.uuid];
                                if (next.length >= gcAgents.length) next = null;
                              }
                              return next;
                            });
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition border ${
                            inBench
                              ? "bg-surface-secondary/50 border-border-secondary hover:bg-interactive-hover"
                              : "border-border-primary hover:bg-interactive-hover opacity-50"
                          }`}
                        >
                          <AgentAvatar agent={agent} size="sm" />
                          <div className="flex-1 text-left min-w-0">
                            <div className="text-sm font-medium text-content-primary">{agent.name}</div>
                            <div className="text-xs text-content-tertiary">{agent.role}</div>
                          </div>
                          <div className="text-xs text-content-quaternary mr-2 hidden sm:block max-w-[200px] truncate">
                            {agent.description}
                          </div>
                          <div
                            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition ${
                              inBench ? "bg-brand-primary border-brand-primary" : "border-border-hover"
                            }`}
                          >
                            {inBench && <Check className="w-3 h-3 text-white" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <p className="text-[11px] text-content-quaternary">
                    Minimum 2 personas required. The orchestrator selects 2-4 per question from your bench.
                  </p>
                  <span className="text-xs text-content-tertiary">
                    {gcBenchUuids === null
                      ? `${gcAgents.length} of ${gcAgents.length} selected`
                      : `${gcBenchUuids.length} of ${gcAgents.length} selected`}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === "notifications" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-content-primary">Notifications</h2>
                <p className="text-content-tertiary mt-1">Configure how you receive updates for each event type</p>
              </div>

              {isLoadingNotifPrefs ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 text-content-tertiary animate-spin" />
                </div>
              ) : (
                <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
                  {/* Table header */}
                  <div className="grid grid-cols-[1fr_80px_80px] gap-4 px-4 py-3 border-b border-border-primary bg-surface-primary/60">
                    <span className="text-xs font-semibold text-content-tertiary uppercase">Event</span>
                    <span className="text-xs font-semibold text-content-tertiary uppercase text-center">In-App</span>
                    <span className="text-xs font-semibold text-content-tertiary uppercase text-center">Desktop</span>
                  </div>

                  {/* Rows */}
                  {NOTIFICATION_TYPES.map(({ type, label, description }) => {
                    const pref = notifPrefs.find((p) => p.notificationType === type);
                    const inAppEnabled = pref?.inAppEnabled ?? true;
                    const desktopEnabled = pref?.desktopEnabled ?? false;

                    return (
                      <div
                        key={type}
                        className="grid grid-cols-[1fr_80px_80px] gap-4 items-center px-4 py-3 border-b border-border-primary last:border-b-0"
                      >
                        <div>
                          <h4 className="text-sm font-medium text-content-primary">{label}</h4>
                          <p className="text-xs text-content-tertiary mt-0.5">{description}</p>
                        </div>

                        {/* In-App toggle */}
                        <div className="flex justify-center">
                          <button
                            onClick={() => handleNotifPrefToggle(type, "inAppEnabled", !inAppEnabled)}
                            className={`relative w-10 h-6 rounded-full transition-colors ${
                              inAppEnabled ? "bg-brand-primary" : "bg-surface-tertiary"
                            }`}
                          >
                            <div
                              className={`absolute top-1 w-4 h-4 rounded-full transition-transform ${
                                inAppEnabled ? "translate-x-5 bg-white" : "translate-x-1 bg-slate-400"
                              }`}
                            />
                          </button>
                        </div>

                        {/* Desktop toggle */}
                        <div className="flex justify-center">
                          <button
                            onClick={() => handleNotifPrefToggle(type, "desktopEnabled", !desktopEnabled)}
                            className={`relative w-10 h-6 rounded-full transition-colors ${
                              desktopEnabled ? "bg-brand-secondary" : "bg-surface-tertiary"
                            }`}
                          >
                            <div
                              className={`absolute top-1 w-4 h-4 rounded-full transition-transform ${
                                desktopEnabled ? "translate-x-5 bg-white" : "translate-x-1 bg-slate-400"
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {desktopPermission === "denied" && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
                  <p className="text-xs text-red-300">
                    Desktop notifications are blocked by your browser. Enable them in your browser&apos;s site settings.
                  </p>
                </div>
              )}

              {desktopPermission === "default" && (
                <div className="flex items-center gap-2 px-4 py-3 bg-surface-secondary border border-border-secondary rounded-lg">
                  <Bell className="h-4 w-4 text-content-tertiary shrink-0" />
                  <p className="text-xs text-content-tertiary">
                    Enabling a desktop toggle will prompt your browser for notification permission.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Security Tab */}
          {activeTab === "security" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-content-primary">Security</h2>
                <p className="text-content-tertiary mt-1">Manage your security settings</p>
              </div>

              <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-content-primary">Microsoft Entra ID</h3>
                    <p className="text-sm text-content-tertiary">Authentication managed by your organization</p>
                  </div>
                </div>
                <p className="text-sm text-content-tertiary">
                  Your account security is managed through Microsoft Entra ID (formerly Azure AD). Contact your IT
                  administrator for password changes or multi-factor authentication settings.
                </p>
              </div>

              <ApiKeysManager />
            </div>
          )}

          {/* RALLY Tab */}
          {activeTab === "harness" && featureFlags.rally && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-content-primary">RALLY</h2>
                <p className="text-content-tertiary mt-1">
                  Remote Access Link &amp; Loopback Yoke — connect always-on machines to run agentic work, and monitor
                  their status.
                </p>
              </div>

              <HarnessWorkersManager />
              <HarnessJobsManager />
            </div>
          )}

          {/* Teams Tab */}
          {activeTab === "teams" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-content-primary">Team Memberships</h2>
                <p className="text-content-tertiary mt-1">Teams you belong to and your roles</p>
              </div>

              <div className="bg-surface-primary border border-border-primary rounded-xl divide-y divide-border-primary">
                {profile?.teams && profile.teams.length > 0 ? (
                  profile.teams.map((membership) => (
                    <div key={membership.team.uuid} className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-linear-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center">
                          <Users className="h-5 w-5 text-brand-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium text-content-primary">{membership.team.name}</h4>
                          <p className="text-xs text-content-tertiary capitalize">{membership.role}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center">
                    <Users className="h-8 w-8 text-content-quaternary mx-auto mb-2" />
                    <p className="text-content-tertiary">No team memberships</p>
                  </div>
                )}
              </div>

              {profile?.teams && profile.teams.length <= 1 && (
                <div className="flex items-start gap-3 px-4 py-3 bg-surface-secondary border border-border-secondary rounded-lg">
                  <Users className="h-4 w-4 text-content-tertiary shrink-0 mt-0.5" />
                  <p className="text-xs text-content-tertiary">
                    Want to collaborate with more teams? Ask your platform administrator to invite you to additional
                    teams, or request an invite from a team admin.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Usage Tab */}
          {activeTab === "integrations" && <SettingsIntegrationsTab />}

          {activeTab === "extensions" && <SettingsExtensionsTab />}

          {activeTab === "authorizations" && <SettingsAuthorizationsTab />}

          {activeTab === "usage" && <SettingsUsageTab />}

          {/* Save Button + Status */}
          {hasSaveableSettings && (
            <div className="mt-6 flex items-center justify-end gap-3">
              {saveStatus === "success" && (
                <div className="flex items-center gap-1.5 text-green-400 text-sm">
                  <Check className="h-4 w-4" />
                  Settings saved
                </div>
              )}
              {saveStatus === "error" && (
                <div className="flex items-center gap-1.5 text-red-400 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {saveError || "Failed to save"}
                </div>
              )}
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
